import { PageLoader } from "@/components/PageLoader";
import { TIPO_INSPECAO } from "@/constants/TIPO_INSPECAO";
import { useAnaliseLaboratorial } from "@/hooks/useAnaliseLaboratorial";
import { IAnaliseLaboratorial } from "@/interfaces/IAnaliseLaboratorial";
import { cpfMask } from "@/utils/cpfMask";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  InputNumber,
  Radio,
  Result,
  Row,
  Space,
  Table,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import styles from "./style.module.css";

export function VisualizacaoAnaliseLaboratorial() {
  const screens = Grid.useBreakpoint();
  const { id } = useParams<{ id: string }>();
  const { getAnaliseLaboratorialByIdApi, getAnexoApi } =
    useAnaliseLaboratorial();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const {
    data: analiseLaboratorial,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["REQUISICAO_ANALISE_LABORATORIAL", id],
    enabled: !!id,
    queryFn: () => getAnaliseLaboratorialByIdApi(Number(id)),
  });

  const columns: ColumnsType<IAnaliseLaboratorial["itens"][number]> = [
    {
      title: "Ensaio",
      dataIndex: ["ensaio", "nome"],
      key: "ensaio",
      width: "40%",
      ellipsis: true,
    },
    {
      title: "Resultado",
      key: "resultado",
      dataIndex: "resultado",
      width: "30%",
      align: "center",
    },
    {
      title: "Unidade",
      dataIndex: ["ensaio", "unidadeMedida", "sigla"],
      key: "unidade",
      align: "center",
      width: "10%",
    },
    {
      title: "Conforme",
      key: "conforme",
      width: "10%",
      align: "center",
      render: (_, record) => <Checkbox checked={record.conforme} disabled />,
    },
    {
      title: "Não Realizada",
      key: "naoRealizada",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Checkbox checked={record.naoRealizada} disabled />
      ),
    },
  ];

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoApi(nomeUpload);
      saveAs(anexoBlob, analiseLaboratorial?.nomeArquivo || "anexo");
    } catch (error) {
      message.error("Não foi possível abrir o anexo.");
    }
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Visualização Requisição de Análise Laboratorial";
  }, []);

  const programacaoItem = analiseLaboratorial?.programacao?.produto
    ? `${analiseLaboratorial.programacao.produto.produto.nome} - ${analiseLaboratorial.programacao.produto.numeroRotulo}`
    : analiseLaboratorial?.programacao?.agua
      ? "Água"
      : "";

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <Result
        status="500"
        title="Parece que aconteceu algum problema com o servidor."
        subTitle="Tente novamente mais tarde ou entre em contato com o suporte."
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
            <Button onClick={() => refetch()}>Tentar Novamente</Button>
          </Space>
        }
      />
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Requisição de Análise Laboratorial {analiseLaboratorial?.codigo}
        </Typography.Title>
      </Space>
      <Divider />

      <Form layout="vertical">
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Estabelecimento">
              <Input
                disabled
                value={`${analiseLaboratorial?.estabelecimento.numeroInspecao} - ${analiseLaboratorial?.estabelecimento ? TIPO_INSPECAO[analiseLaboratorial.estabelecimento.tipoInspecao] : "SISE"} - ${analiseLaboratorial?.estabelecimento.nome}`}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Data Coleta">
              <DatePicker
                format={"DD/MM/YYYY HH:mm"}
                showTime
                disabled
                style={{ width: "100%" }}
                value={
                  analiseLaboratorial?.dataColeta
                    ? dayjs(analiseLaboratorial.dataColeta)
                    : undefined
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Etapa" rules={[{ required: true }]}>
              <Input disabled value={analiseLaboratorial?.etapaColeta || ""} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item>
              <Checkbox disabled checked={analiseLaboratorial?.fiscal}>
                Análise Fiscal
              </Checkbox>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Natureza Analise">
              <Radio.Group disabled value={analiseLaboratorial?.natureza}>
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={24} md={24}>
                    <Radio value="MICROBIOLOGICA">MICROBIOLOGIA</Radio>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <Radio value="FISICOQUIMICO">FÍSICO QUÍMICA</Radio>
                  </Col>
                </Row>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Condições Amostra">
              <Radio.Group
                disabled
                value={analiseLaboratorial?.condicaoAmostra}
              >
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Radio value="CONGELADO">CONGELADO SÓLIDO</Radio>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Radio value="CRISTAIS">CRISTAIS DE GELO</Radio>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Radio value="RESFRIADO">RESFRIADO</Radio>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Radio value="AMBIENTE">AMBIENTE</Radio>
                  </Col>
                </Row>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Programação">
              <Input
                disabled
                value={
                  analiseLaboratorial?.programacao
                    ? `${dayjs(analiseLaboratorial?.programacao.dataProgramada).format("DD/MM/YYYY")} - ${programacaoItem}`
                    : ""
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={16} md={12}>
            <Form.Item label="Produto">
              <Input
                disabled
                value={
                  analiseLaboratorial?.produto
                    ? `${analiseLaboratorial?.produto?.produto?.nome} - ${analiseLaboratorial?.produto?.numeroRotulo}`
                    : ""
                }
              />
            </Form.Item>
          </Col>

          <Col
            xs={24}
            sm={8}
            md={4}
            style={{ display: "flex", alignItems: "end" }}
          >
            <Form.Item>
              <Checkbox disabled checked={analiseLaboratorial?.agua}>
                Água
              </Checkbox>
            </Form.Item>
          </Col>

          {analiseLaboratorial?.justificativa && (
            <Col xs={24}>
              <Form.Item label="Justificativa">
                <Input.TextArea
                  disabled
                  value={analiseLaboratorial?.justificativa}
                />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Informações Adicionais">
              <Input disabled value={analiseLaboratorial?.infAdicional} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Marca">
              <Input disabled value={analiseLaboratorial?.marca?.nome} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Data Fabricação">
              <DatePicker
                format={"DD/MM/YYYY"}
                disabled
                style={{ width: "100%" }}
                value={
                  analiseLaboratorial?.dataFabricacao
                    ? dayjs(analiseLaboratorial.dataFabricacao)
                    : undefined
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Data Validade">
              <DatePicker
                format={"DD/MM/YYYY"}
                style={{ width: "100%" }}
                disabled
                value={
                  analiseLaboratorial?.dataValidade
                    ? dayjs(analiseLaboratorial.dataValidade)
                    : undefined
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Lote">
              <Input disabled value={analiseLaboratorial?.lote} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Tamanho da Partida">
              <Input disabled value={analiseLaboratorial?.tamanhoPartida} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Lacre">
              <Input disabled value={analiseLaboratorial?.lacre} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Lacre Contra Prova Empresa">
              <Input disabled value={analiseLaboratorial?.lacreCProvaEmp} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Lacre Contra Prova SISE">
              <Input disabled value={analiseLaboratorial?.lacreCProvaSise} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Data Remessa">
              <DatePicker
                format={"DD/MM/YYYY"}
                disabled
                style={{ width: "100%" }}
                value={
                  analiseLaboratorial?.dataRemessa
                    ? dayjs(analiseLaboratorial.dataRemessa)
                    : undefined
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Temperatura da Amostra na Coleta">
              <Input
                disabled
                suffix="°C"
                value={analiseLaboratorial?.temperaturaColeta}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Observação Coleta">
              <Input.TextArea
                disabled
                value={analiseLaboratorial?.observacaoColeta}
              />
            </Form.Item>
          </Col>

          <Divider orientation="left">
            <Typography.Title level={5}>Ensaios</Typography.Title>
          </Divider>

          <Col xs={24}>
            <Form.Item rules={[{ required: true }]}>
              <Input
                disabled
                value={analiseLaboratorial?.itens
                  .map((ensaio) => ensaio.ensaio.nome)
                  .join(", ")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Col xs={24} sm={24}>
          <Form.Item>
            <Radio.Group
              disabled
              value={analiseLaboratorial?.statusAssinaturaFiscalizado}
            >
              <Radio value="ASSINADO_FISICAMENTE">Assinatura Fisica</Radio>

              <Radio value="ASSINADO_DIGITALMENTE">Assinatura Digital</Radio>

              <Radio value="RECUSADO">Recusado</Radio>

              <Radio value="AUSENTE">Ausente</Radio>

              <Radio value="IMPOSSIBILITADO">Impossibilitado</Radio>

              <Radio value="NAO_ALFABETIZADO">Não Alfabetizado</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {analiseLaboratorial?.statusAssinaturaFiscalizado &&
          analiseLaboratorial?.statusAssinaturaFiscalizado !==
            "ASSINADO_FISICAMENTE" && (
            <Row
              style={{ marginTop: 20 }}
              gutter={[24, 24]}
              justify={"space-around"}
            >
              <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                <Row justify={"center"} style={{ marginTop: 10 }}>
                  <Col
                    span={24}
                    style={{
                      border: "1px solid #ccc",
                      height: 400,

                      borderRadius: 8,
                    }}
                  >
                    <Form.Item>
                      {analiseLaboratorial?.assinaturaServidor && (
                        <img
                          src={analiseLaboratorial.assinaturaServidor}
                          alt="Assinatura Servidor"
                          style={{ width: "90%", height: "90%" }}
                        />
                      )}
                    </Form.Item>

                    <Form.Item name="assinaturaServidor" hidden>
                      <Input type="hidden" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row
                  justify={"center"}
                  style={{ marginTop: 10 }}
                  gutter={[8, 8]}
                >
                  <Col span={24} style={{ textAlign: "center" }}>
                    <Typography.Text strong>
                      Assinatura Responsável Técnico
                    </Typography.Text>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item label="Nome">
                      <Input
                        disabled
                        value={analiseLaboratorial?.responsavelColeta?.nome}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item label="CPF">
                      <Input
                        disabled
                        value={cpfMask(
                          analiseLaboratorial?.responsavelColeta?.cpfCnpj || ""
                        )}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item label="Função">
                      <Input disabled value={"RESPONSÁVEL COLETA"} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {analiseLaboratorial?.statusAssinaturaFiscalizado ===
                "ASSINADO_DIGITALMENTE" && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                  <Row justify={"center"} style={{ marginTop: 10 }}>
                    <Col
                      span={24}
                      style={{
                        border: "1px solid #ccc",
                        height: 400,
                        borderRadius: 8,
                      }}
                    >
                      <Form.Item>
                        {analiseLaboratorial.assinaturaFiscalizado && (
                          <img
                            src={analiseLaboratorial.assinaturaFiscalizado}
                            alt="Assinatura Fiscalizado"
                            style={{ width: "100%", height: "100%" }}
                          />
                        )}
                      </Form.Item>

                      <Form.Item name="assinaturaFiscalizado" hidden>
                        <Input type="hidden" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row
                    justify={"center"}
                    style={{ marginTop: 10 }}
                    gutter={[8, 8]}
                  >
                    <Col span={24} style={{ textAlign: "center" }}>
                      <Typography.Text strong>
                        Assinatura Fiscalizado
                      </Typography.Text>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Form.Item label="Nome">
                        <Input
                          disabled
                          value={analiseLaboratorial.nomeAssinanteFiscalizado}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Form.Item label="CPF">
                        <Input
                          disabled
                          value={cpfMask(
                            analiseLaboratorial.cpfAssinanteFiscalizado || ""
                          )}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Form.Item label="Função">
                        <Input
                          disabled
                          value={analiseLaboratorial.funcaoAssinanteFiscalizado}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
          )}

        {analiseLaboratorial?.situacao === "RECEBIDO" && (
          <>
            <Divider orientation="left">
              <Typography.Title level={screens.xs ? 5 : 4}>
                Certificado
              </Typography.Title>
            </Divider>

            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item label="Laboratório">
                  <Input
                    disabled
                    value={analiseLaboratorial?.laboratorio?.nome || ""}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Nº Registro Laboratório">
                  <Input
                    disabled
                    value={analiseLaboratorial?.numeroRegistroLaboratorio || ""}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Data Recebimento">
                  <DatePicker
                    format="DD/MM/YYYY"
                    disabled
                    value={
                      analiseLaboratorial?.dataRecebimento
                        ? dayjs(analiseLaboratorial.dataRecebimento)
                        : undefined
                    }
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label={" "}>
                  <Checkbox
                    checked={analiseLaboratorial?.amostraImpropria}
                    disabled
                  >
                    Amostra Imprória
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label="Observação Recebimento">
                  <Input.TextArea
                    disabled
                    value={analiseLaboratorial?.observacaoRecebimento || ""}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Anexo">
                  {analiseLaboratorial?.nomeArquivo && (
                    <Button
                      type="link"
                      onClick={() =>
                        handleOpenAnexo(analiseLaboratorial.nomeUpload)
                      }
                    >
                      {analiseLaboratorial.nomeArquivo}
                    </Button>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">
              <Typography.Title level={5}>
                Ensaios do Certificado
              </Typography.Title>
            </Divider>

            {analiseLaboratorial.produto?.produto.exigeResultadoUmidade &&
              analiseLaboratorial?.natureza === "MICROBIOLOGICA" && (
                <Row>
                  <Col xs={24}>
                    <Form.Item label="Resultado Umidade">
                      <InputNumber
                        style={{ width: "200px" }}
                        disabled
                        value={analiseLaboratorial?.resultadoUmidade || ""}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

            <Row>
              <Col xs={24}>
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={analiseLaboratorial?.itens || []}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: true }}
                  className={styles.customTable}
                />
              </Col>
            </Row>
          </>
        )}

        <Row justify={"center"} gutter={[10, 10]} style={{ marginTop: 20 }}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ width: 200 }}
            >
              Voltar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
